<script setup>
import { useStore } from 'vuex'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import FilterChipGroup from '@/components/boundary-condition/FilterChipGroup.vue'
import DropDownSingleSelect from '@/components/form/DropDownSingleSelect.vue'
import { computed, onMounted, toRef, watch } from 'vue'
import { actionMenuMixin } from '@/mixins/actionMenuMixin'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'
import { boundaryConditionTableMixin } from '@/mixins/boundaryConditionTableMixin'

const STORE_NAMESPACE = 'hydraulicHeadBoundaryCondition'
const NULL_VALUE = -999

const store = useStore()

const props = defineProps({
  items: { type: Array, required: true },
  years: { type: Array, required: true },
  count: { type: Number, require: true },
  editable: { require: false, default: true },
  lineCoords: { type: Array, require: true }
})

const items = toRef(props, 'items')

const { x, y, showMenu, selectedItem, show } = actionMenuMixin()
const { dirtyItems, addDirty, getDirtyClass } = dirtyFieldMixin()

const deleteFromState = (item) => {
  store.dispatch('settlement/deleteFromState', item)
}

const deleteItems = (input) => {
  store.dispatch('settlement/deleteByReferencePoint', input)
}

const emit = defineEmits(['changePage'])

const {
  itemPerPage,
  referencePointsOptions,
  referencePointsShow,
  activeYears,
  filterYearId,
  borderBottom,
  updateOptions,
  deleteReferencePoint
} = boundaryConditionTableMixin(
  STORE_NAMESPACE,
  items,
  selectedItem,
  getDirtyClass,
  deleteItems,
  deleteFromState,
  emit
)

const loading = computed(() => store.state[STORE_NAMESPACE].loading)

watch(
  () => props.years,
  () => {
    activeYears.value = JSON.parse(JSON.stringify(props.years))
  }
)

const setReferencePoint = (input) => {
  store.dispatch('hydraulicHeadBoundaryCondition/setReferencePoint', input)
}

const setNestedParameter = (input) => {
  store.dispatch('hydraulicHeadBoundaryCondition/setNestedParameter', input)
}

const value = (item, index) => {
  let parameter = `geometry.coordinates.${index}.1`
  let arr = parameter.split('.')
  let value = item
  for (let i = 0; i < arr.length; i++) {
    if (value) value = value[arr[i]]
  }
  if (value === NULL_VALUE || Number(value) === NULL_VALUE) {
    return ''
  }
  return value
}
const createFilters = () => {
  activeYears.value = JSON.parse(JSON.stringify(props.years))
}
const setValue = (input) => {
  let value = input.value
  if (value === '' || value === undefined || value === null) {
    value = NULL_VALUE
  }
  setNestedParameter({ item: input.item, value: value, parameter: input.parameter })
}
defineExpose({
  dirtyItems
})

onMounted(() => {
  createFilters()
})
</script>

<template>
  <div>
    <div style="justify-content: space-between; display: flex">
      <v-card variant="flat" v-if="years.length">
        <FilterChipGroup :items="years" :active-items="activeYears" parameter="year" />
      </v-card>
      <slot></slot>
    </div>
    <v-row>
      <v-col>
        <v-data-table-server
          :items-length="count ?? 0"
          :items="referencePointsShow"
          density="compact"
          hide-no-data
          :items-per-page="itemPerPage"
          @update:options="updateOptions"
          :footer-props="{
            disableItemsPerPage: true
          }"
        >
          <template #headers>
            <tr>
              <th style="min-width: 200px"></th>
              <template v-if="activeYears">
                <th v-for="year in years" :key="year.id" :style="filterYearId(year.id)">
                  Hydraulic head {{ year.year }}
                </th>
              </template>
            </tr>
            <tr>
              <th>
                <span class="pl-1">Reference point</span>
              </th>
              <th
                v-for="year in years"
                :key="year.id"
                :style="filterYearId(year.id)"
                class="year-header"
              >
                <div
                  v-for="coord in lineCoords"
                  :key="coord.id"
                  class="pa-0"
                  style="display: table-cell; min-width: 75px"
                >
                  {{ coord[0] }}
                </div>
              </th>
            </tr>
          </template>

          <template #body>
            <tr v-if="loading" class="v-data-table-progress">
              <th colspan="999" style="text-align: center">
                <v-progress-linear indeterminate color="primary" />
              </th>
            </tr>
            <template v-else>
              <tr
                v-for="referencePoint in referencePointsShow"
                :key="referencePoint.id"
                @contextmenu="show($event, referencePoint)"
              >
                <td>
                  <DropDownSingleSelect
                    @update="setReferencePoint"
                    @addDirty="addDirty(referencePoint, referencePoint.id)"
                    :class="borderBottom(referencePoint)"
                    :options="referencePointsOptions"
                    :item="referencePoint.id"
                    :editable="editable"
                  />
                </td>

                <td
                  v-for="item in referencePoint.hydraulicheadboundarycondition_set"
                  :key="item.id"
                  :style="filterYearId(item.properties.year)"
                >
                  <div
                    v-for="(coordinate, index) in item.geometry.coordinates"
                    :key="coordinate.id"
                    class="pa-0"
                    style="display: table-cell"
                  >
                    <v-text-field
                      full-width
                      :class="`${getDirtyClass(item, `${coordinate[0]}_1`)}`"
                      style="min-width: 100%"
                      hide-details
                      solo
                      variant="plain"
                      density="compact"
                      :readonly="!editable"
                      type="number"
                      @update:model-value="
                        addDirty(item, `${coordinate[0]}_1`),
                          setValue({
                            item: item,
                            value: $event,
                            parameter: `geometry.coordinates.${index}.1`
                          })
                      "
                      :model-value="value(item, index)"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </v-data-table-server>
      </v-col>
    </v-row>
    <TableActionMenu
      @update:show-menu="showMenu = $event"
      v-if="editable"
      :add-button="false"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      :y="y"
      @action="deleteReferencePoint()"
    />
  </div>
</template>

<style scoped>
.v-table :deep(th) {
  border-bottom: 1px solid rgba(255, 255, 255, 0) !important;
  text-align: left;
  padding: 0 !important;
  font-size: 0.75rem;
  font-family: Roboto, sans-serif;
  font-weight: bold !important;
  color: rgba(0, 0, 0, 0.6);
}

.v-table :deep(td) {
  padding: 0 !important;
}

.v-table :deep(.v-field__input) {
  padding-top: 0;
  padding-bottom: 0;
}

:deep(.v-field__field) input {
  font-size: 14px;
}

.v-table:deep(th) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.year-header {
  min-width: 75px;
}
</style>
